/**
 * The app's one MeshCore radio session.
 *
 * Owns the connection (BLE today), maps rooms to private channel slots on the
 * radio, and paces everything that goes on air. Rooms subscribe for their own
 * text; any number of listeners may watch status (no global handler slot to
 * clobber).
 *
 * Airtime: on the Sacramento preset (SF9 / 62.5 kHz) a full 150-byte frame is
 * ~1.5 s on air, and the channel is shared with the whole community mesh.
 * Frames therefore leave one at a time, at least MIN_FRAME_SPACING_MS apart,
 * each confirmed radiated before the next.
 *
 * Legality: the session never transmits while the radio REPORTS a channel
 * outside the chosen region's band (MeshCore firmware ships on EU 869 MHz).
 * Radio settings are changed only through applyRadio(), which uses the
 * sequence proven on the MeshBell bench: fresh connection, SET_RADIO first,
 * reconnect, read the settings back. An OK is never taken as proof.
 */

import { MeshCoreCompanion, CompanionError, type CompanionTransport, type SendResult } from './client'
import type { ChannelMessage, CompanionFrame, DeviceInfo, RadioParams, SelfInfo } from './companion'
import { deriveRoomChannel } from './room-channel'
import { chooseBleRadio, openBleCompanionOn } from './ble-transport'
import { REGIONS, channelEdges, isInsideBand, type MeshCoreProfile } from '../lora/profiles'

export const MIN_FRAME_SPACING_MS = 5000
/** Never hold more than this many unsent frames; older ones are dropped first. */
export const MAX_QUEUED_FRAMES = 10

export type RadioStatus = 'disconnected' | 'connecting' | 'configuring' | 'scanning' | 'ready' | 'error'

export interface RadioState {
  status: RadioStatus
  selfInfo: SelfInfo | null
  deviceInfo: DeviceInfo | null
  error: string | null
  /** Region the user chose; transmit is refused outside its band. */
  region: string
  /** Why transmit is refused right now, or null. */
  bandProblem: string | null
  /** Frames confirmed on air / sent but not confirmed, since connect. */
  sent: number
  unconfirmed: number
  queued: number
}

export interface RoomRadioText {
  roomId: string
  text: string
  senderTimestamp: number
  snr?: number
  pathLen?: number
}

type Opener = (onLinkLost: () => void) => Promise<CompanionTransport>

export interface ScanResult {
  profileId: string
  label: string
  /** Any frame the radio heard on this channel during the listen. */
  framesHeard: number
  /** Nodes that answered our zero-hop discover. */
  discoverReplies: number
  adverts: number
  /** Strongest frame heard, dBm, or null if nothing. */
  bestRssi: number | null
  error?: string
}

export interface ScanProgress {
  index: number
  total: number
  label: string
}

interface RoomSlot {
  index: number
  secretHex: string
}

interface Outgoing {
  roomId: string
  text: string
  resolve: (r: SendResult) => void
  reject: (e: Error) => void
}

export class MeshCoreSession {
  constructor(private readonly frameSpacingMs = MIN_FRAME_SPACING_MS) {}

  private companion: MeshCoreCompanion | null = null
  private opener: Opener | null = null
  private closingOnPurpose = false
  private state: RadioState = blankState('us_915')
  private rooms = new Map<string, RoomSlot>()
  /** Channel secrets this app put on the radio (survives reconnects). */
  private ownedSecrets = new Set<string>()
  private stateListeners = new Set<(s: RadioState) => void>()
  private textListeners = new Map<string, Set<(m: RoomRadioText) => void>>()
  private queue: Outgoing[] = []
  private pumping = false
  private lastFrameAt = 0
  private pushSink: ((f: CompanionFrame) => void) | null = null

  get snapshot(): RadioState {
    return { ...this.state, queued: this.queue.length }
  }

  onState(cb: (s: RadioState) => void): () => void {
    this.stateListeners.add(cb)
    cb(this.snapshot)
    return () => this.stateListeners.delete(cb)
  }

  // ----- connection -----

  /** Pick and connect a radio over Bluetooth. Call from a user gesture. */
  async connect(): Promise<SelfInfo> {
    const device = await chooseBleRadio()
    return this.connectWith(async (onLost) => (await openBleCompanionOn(device, onLost)).transport)
  }

  /**
   * Connect over any transport (serial on a base station, a fake in tests).
   * `open` may be called again later to reconnect to the same radio.
   */
  async connectWith(open: Opener): Promise<SelfInfo> {
    if (this.companion) await this.disconnect()
    this.opener = open
    this.update({ status: 'connecting', error: null, sent: 0, unconfirmed: 0 })
    try {
      const info = await this.openCompanion()
      this.update({ status: 'ready' })
      return info
    } catch (e) {
      this.update({ status: 'error', error: messageOf(e) })
      throw e
    }
  }

  async disconnect(): Promise<void> {
    this.failQueue('radio disconnected')
    await this.closeCompanion()
    this.opener = null
    this.rooms.clear()
    this.update({ ...blankState(this.state.region) })
  }

  // ----- region + radio settings -----

  /** Choose the legal region. Transmit is refused while the radio sits outside it. */
  setRegion(code: string): void {
    if (!REGIONS[code]) throw new Error(`unknown region ${code}`)
    this.update({ region: code })
  }

  /**
   * Retune the radio and prove it took. Rooms re-attach on 'ready'.
   * Returns what the radio REPORTS afterwards.
   */
  async applyRadio(params: RadioParams): Promise<SelfInfo> {
    if (!this.opener) throw new CompanionError('no radio connected')
    if (!isInsideBand(this.state.region, params.freqMHz, params.bwKHz)) {
      throw new CompanionError(`refusing: ${params.freqMHz} MHz is outside ${REGIONS[this.state.region]?.displayName}`)
    }
    this.failQueue('radio is being retuned')
    this.rooms.clear()
    this.update({ status: 'configuring', error: null })
    try {
      const info = await this.retune(params)
      this.update({ status: 'ready' })
      return info
    } catch (e) {
      this.update({ status: this.companion ? 'ready' : 'error', error: messageOf(e) })
      throw e
    }
  }

  /**
   * "Find a mesh": tune to each legal MeshCore profile in turn, send one
   * zero-hop discover, listen, and count what answers. The radio is off its
   * own network while this runs, and is ALWAYS put back afterwards.
   * Results are ranked most-alive first.
   */
  async scan(
    profiles: readonly MeshCoreProfile[],
    opts: { listenMs?: number; onProgress?: (p: ScanProgress) => void } = {}
  ): Promise<ScanResult[]> {
    const original = this.state.selfInfo?.radio
    if (!this.opener || !original) throw new CompanionError('no radio connected')
    const listenMs = opts.listenMs ?? 30_000
    const candidates = profiles.filter(
      (p) =>
        p.firmware === 'meshcore' &&
        p.region === this.state.region && // e.g. AU/NZ presets are in-band in the US but are not US meshes
        isInsideBand(this.state.region, p.freqMHz, p.bwKHz)
    )
    this.failQueue('radio is scanning for a mesh')
    this.rooms.clear()
    this.update({ status: 'scanning', error: null })
    const results: ScanResult[] = []
    try {
      for (const [i, p] of candidates.entries()) {
        opts.onProgress?.({ index: i, total: candidates.length, label: p.label })
        const r: ScanResult = { profileId: p.id, label: p.label, framesHeard: 0, discoverReplies: 0, adverts: 0, bestRssi: null }
        try {
          await this.retune({ freqMHz: p.freqMHz, bwKHz: p.bwKHz, sf: p.sf, cr: p.cr })
          this.pushSink = (f) => {
            if (f.kind === 'push_log_rx') {
              r.framesHeard++
              r.bestRssi = r.bestRssi === null ? f.rssi : Math.max(r.bestRssi, f.rssi)
            } else if (f.kind === 'push_control') {
              r.discoverReplies++
            } else if (f.kind === 'push_advert') {
              r.adverts++
            }
          }
          await this.companion!.discoverNodes().catch(() => undefined)
          await new Promise((res) => setTimeout(res, listenMs))
        } catch (e) {
          r.error = messageOf(e)
        } finally {
          this.pushSink = null
        }
        results.push(r)
      }
    } finally {
      // Put the radio back where it was, whatever happened above.
      try {
        await this.retune(original)
        this.update({ status: 'ready' })
      } catch (e) {
        this.update({ status: this.companion ? 'ready' : 'error', error: `scan could not restore the radio: ${messageOf(e)}` })
      }
    }
    const score = (x: ScanResult) => x.discoverReplies * 3 + x.adverts * 2 + x.framesHeard
    return results.sort((a, b) => score(b) - score(a))
  }

  /** Fresh connection, SET_RADIO, reconnect, read back. Throws unless the radio took it. */
  private async retune(params: RadioParams): Promise<SelfInfo> {
    await this.closeCompanion()
    await this.openCompanion()
    await this.companion!.setRadio(params)
    await this.closeCompanion()
    const info = await this.openCompanion()
    const r = info.radio
    const took =
      Math.round(r.freqMHz * 1000) === Math.round(params.freqMHz * 1000) &&
      Math.round(r.bwKHz * 1000) === Math.round(params.bwKHz * 1000) &&
      r.sf === params.sf &&
      r.cr === params.cr
    if (!took) {
      throw new CompanionError(
        `the radio did not take the settings: it reports ${r.freqMHz} MHz / ${r.bwKHz} kHz / SF${r.sf} / CR${r.cr}`
      )
    }
    return info
  }

  // ----- rooms -----

  /** Put a room's private channel on the radio (idempotent). */
  async joinRoom(roomId: string, roomSecret: string): Promise<number> {
    const existing = this.rooms.get(roomId)
    if (existing) return existing.index
    const c = this.requireCompanion()
    const ch = await deriveRoomChannel(roomSecret, roomId)
    const slot = await c.ensureChannel(ch.name, ch.secret)
    if (slot.created) this.ownedSecrets.add(ch.secretHex)
    this.rooms.set(roomId, { index: slot.index, secretHex: ch.secretHex })
    return slot.index
  }

  /** Take the room's channel off the radio if this app put it there. */
  async leaveRoom(roomId: string): Promise<void> {
    const slot = this.rooms.get(roomId)
    this.rooms.delete(roomId)
    this.queue = this.queue.filter((o) => {
      if (o.roomId !== roomId) return true
      o.reject(new Error('left the room'))
      return false
    })
    if (slot && this.ownedSecrets.has(slot.secretHex) && this.companion) {
      await this.companion.clearChannel(slot.index).catch(() => undefined)
      this.ownedSecrets.delete(slot.secretHex)
    }
  }

  isRoomJoined(roomId: string): boolean {
    return this.rooms.has(roomId)
  }

  /** Bytes one frame of text may carry on the connected radio. */
  textBudget(): number {
    return this.requireCompanion().textBudget()
  }

  onRoomText(roomId: string, cb: (m: RoomRadioText) => void): () => void {
    let set = this.textListeners.get(roomId)
    if (!set) this.textListeners.set(roomId, (set = new Set()))
    set.add(cb)
    return () => set!.delete(cb)
  }

  /** Queue text for the room's channel. Resolves once it radiated (or didn't). */
  sendRoomText(roomId: string, text: string): Promise<SendResult> {
    if (!this.rooms.has(roomId)) return Promise.reject(new Error('room is not on the radio'))
    if (this.state.bandProblem) return Promise.reject(new Error(this.state.bandProblem))
    return new Promise((resolve, reject) => {
      this.queue.push({ roomId, text, resolve, reject })
      while (this.queue.length > MAX_QUEUED_FRAMES) {
        this.queue.shift()?.reject(new Error('dropped: radio queue full'))
      }
      this.update({})
      void this.pump()
    })
  }

  // ----- internals -----

  private async openCompanion(): Promise<SelfInfo> {
    const transport = await this.opener!(() => this.handleLinkLost())
    const companion = new MeshCoreCompanion(transport, { commandTimeoutMs: 8000 })
    companion.onChannelMessage((m) => this.dispatch(m))
    companion.onPush((f) => this.pushSink?.(f))
    try {
      const info = await companion.start()
      this.companion = companion
      this.update({ selfInfo: info, deviceInfo: companion.deviceInfo })
      return info
    } catch (e) {
      await companion.close().catch(() => undefined)
      throw e
    }
  }

  private async closeCompanion(): Promise<void> {
    const c = this.companion
    this.companion = null
    if (!c) return
    this.closingOnPurpose = true
    try {
      await c.close().catch(() => undefined)
    } finally {
      this.closingOnPurpose = false
    }
  }

  private async pump(): Promise<void> {
    if (this.pumping) return
    this.pumping = true
    try {
      while (this.queue.length && this.companion) {
        const wait = this.lastFrameAt + this.frameSpacingMs - Date.now()
        if (wait > 0) await new Promise((r) => setTimeout(r, wait))
        const next = this.queue.shift()
        if (!next) break
        const slot = this.rooms.get(next.roomId)
        if (!slot || !this.companion) {
          next.reject(new Error('room is not on the radio'))
          continue
        }
        if (this.state.bandProblem) {
          next.reject(new Error(this.state.bandProblem))
          continue
        }
        try {
          const result = await this.companion.sendChannelText(slot.index, next.text)
          this.lastFrameAt = Date.now()
          if (result.radiated) this.state.sent++
          else this.state.unconfirmed++
          next.resolve(result)
        } catch (e) {
          next.reject(e instanceof Error ? e : new Error(String(e)))
        }
        this.update({})
      }
    } finally {
      this.pumping = false
    }
  }

  private dispatch(m: ChannelMessage): void {
    for (const [roomId, slot] of this.rooms) {
      if (slot.index !== m.channelIndex) continue
      const listeners = this.textListeners.get(roomId)
      if (!listeners) return
      const msg: RoomRadioText = {
        roomId,
        text: m.text,
        senderTimestamp: m.senderTimestamp,
        snr: m.snr,
        pathLen: m.pathLen,
      }
      for (const l of listeners) l(msg)
      return
    }
  }

  private handleLinkLost(): void {
    if (this.closingOnPurpose) return
    this.companion = null
    this.failQueue('radio link lost')
    this.rooms.clear()
    this.update({ status: 'disconnected', error: 'Radio link lost. Reconnect to keep texting over LoRa.' })
  }

  private failQueue(reason: string): void {
    const q = this.queue
    this.queue = []
    for (const o of q) o.reject(new Error(reason))
  }

  private requireCompanion(): MeshCoreCompanion {
    if (!this.companion) throw new Error('no radio connected')
    return this.companion
  }

  private update(patch: Partial<RadioState>): void {
    const next = { ...this.state, ...patch }
    next.bandProblem = bandProblem(next)
    this.state = next
    const snap = this.snapshot
    for (const l of this.stateListeners) l(snap)
  }
}

function bandProblem(s: RadioState): string | null {
  const r = s.selfInfo?.radio
  if (!r) return null
  if (isInsideBand(s.region, r.freqMHz, r.bwKHz)) return null
  const region = REGIONS[s.region]
  const [lo, hi] = channelEdges(r.freqMHz, r.bwKHz)
  return (
    `Not transmitting: your radio is set to ${lo.toFixed(3)}-${hi.toFixed(3)} MHz, which is outside ` +
    `${region?.displayName ?? s.region}. Choose a network profile to retune it.`
  )
}

function blankState(region: string): RadioState {
  return {
    status: 'disconnected',
    selfInfo: null,
    deviceInfo: null,
    error: null,
    region,
    bandProblem: null,
    sent: 0,
    unconfirmed: 0,
    queued: 0,
  }
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** The single radio session for this app instance. */
export const meshcoreSession = new MeshCoreSession()
